import { useMatchRoute } from "@tanstack/react-router";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Button, DropdownParts, Icon } from "src/components";
import { useCustomerSettingsContext } from "src/contexts";
import { APP_CONFIG, TRANSLATION } from "src/models/constants";
import type { FeatureProps } from "src/models/types/userSettings.types";
import { v4 as uuidv4 } from "uuid";
import { useRootPageContext } from "../RootPage.context";
import styling from "./SideMenu.module.scss";
import { Flex } from "@scaleaq/sui-react";

export const SideMenu = () => {
	// *****************************************
	// Properties
	// *****************************************

	const { username, logout, setSideMenuOpen } = useRootPageContext();
	const { setTheme, theme } = useCustomerSettingsContext();
	const ref = useRef<HTMLDivElement>(null);
	const matchRoute = useMatchRoute();
	const { t } = useTranslation([TRANSLATION.COMMON]);

	// *****************************************
	// Lifecycle
	// *****************************************

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				setSideMenuOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [setSideMenuOpen]);

	// *****************************************
	// Render
	// *****************************************

	const _renderLinkOption = (value: FeatureProps) => {
		const isActive = !!matchRoute({ to: value.path as any, fuzzy: false });

		return (
			<Button
				className={clsx(styling.item, {
					[styling["item--not-active"]]: !isActive,
				})}
				color="neutral"
				variant="ghost"
				key={uuidv4()}
				state={isActive ? "active" : "default"}
				href={value.path}
				onClick={() => {
					setSideMenuOpen(false);
				}}
				leftIcon={value?.icon ? <Icon size="large" name={value.icon} /> : undefined}
				text={t(value.name as any)}
				size="large"
			/>
		);
	};

	const _renderOptions = () => {
		const topOptions: any[] = [];
		const bottomOptions: any[] = [];

		if (APP_CONFIG.features) {
			Object.entries(APP_CONFIG.features).forEach(([_key, value]) => {
				if (value.enabled && value.addToSideMenu) {
					if (value.priority === "top") {
						topOptions.push(_renderLinkOption(value));
					} else {
						bottomOptions.push(_renderLinkOption(value));
					}
				}
			});
		}

		return { topOptions, bottomOptions };
	};

	return (
		<Flex.Column
			className={styling["side-menu-wrapper"]}
			alignment="top-left"
			justifyContent="space-between"
			ref={ref}
		>
			<Button
				className={styling["close-button"]}
				leftIcon={<Icon name="SmallArrowLeft" />}
				paddingRule="icon-only"
				color={"neutral"}
				variant="bordered"
				onClick={() => {
					setSideMenuOpen(false);
				}}
			/>
			<Flex.Column
				render={<nav />}
				className={styling.top}
				alignment="top-center"
				gap={"large"}
				width="100%"
			>
				{_renderOptions().topOptions}
			</Flex.Column>
			<Flex.Column
				render={<nav />}
				className={styling.bottom}
				alignment="bottom-center"
				gap={"large"}
				width="100%"
			>
				{_renderOptions().bottomOptions}
				<DropdownParts.Root
					className={clsx(styling.item, {
						[`${styling["item--collapsed"]}`]: !open,
					})}
				>
					<DropdownParts.Trigger
						leftIcon={<Avatar interactive label={username ?? ""} size="small" color="info" />}
						size={"large"}
						color={"action"}
						variant={"ghost"}
						className={styling["avatar-trigger"]}
						text={username ?? ""}
					/>
					<DropdownParts.Content padded spacing="large">
						<DropdownParts.Item
							color="neutral"
							variant={"ghost"}
							leftIcon={<Icon size="large" name="LargeSettingsUser" href="/settings/general" />}
						>
							{t("COMMON:features.preferences")}
						</DropdownParts.Item>
						<DropdownParts.Item
							color="neutral"
							variant={"ghost"}
							leftIcon={<Icon size="large" name="LargeLogout" onClick={logout} />}
						>
							{t("COMMON:log-out")}
						</DropdownParts.Item>
						<DropdownParts.Item
							color="neutral"
							variant={"ghost"}
							leftIcon={
								<Icon
									size="large"
									name="LargeSunny"
									onClick={() => {
										setTheme(theme === "light" ? "dark" : "light");
									}}
								/>
							}
						>
							{t("COMMON:change-theme")}
						</DropdownParts.Item>
					</DropdownParts.Content>
				</DropdownParts.Root>
			</Flex.Column>
		</Flex.Column>
	);
};
